import React, { useEffect, useRef, useState } from "react";

import { MoreOutlined } from "@ant-design/icons";
import loadable from "@loadable/component";
import { Tooltip, Button, Dropdown } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import useSearchParams from "@/hooks/useSearchParams";

const Paginator = loadable(() => import("@/common/Table/Paginator"));
const Spin = loadable(() => import("@/common/Spin"));
const Table = loadable(() => import("@/common/Table"));

const TABLE_PAGINATION_MARGIN = 32;
const TABLE_HEADER_HEIGHT = 40;

const TableView = ({
  actionMenus,
  onActionMenu,
  onChange,
  hidePagination,
  noHeightLimit,
  loading,
  ...props
}) => {
  const { t } = useTranslation();
  const tableContainerRef = useRef();
  const tableRef = useRef();
  const [tableScrollY, setTableScrollY] = useState();
  const { updateSearchParams } = useSearchParams();

  const tableProps = {
    ...props,
    columns: [
      ...props.columns,
      ...(!!actionMenus && actionMenus.length > 0
        ? [
            {
              width: 50,
              render: (_, record) => (
                <Dropdown
                  menu={{
                    items: actionMenus.map((item) => ({
                      ...item,
                      onClick: (e) => onActionMenu?.(e.key.toString(), record),
                    })),
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Tooltip title={t("common.actionsTitle")}>
                    <Button shape="circle" icon={<MoreOutlined />} />
                  </Tooltip>
                </Dropdown>
              ),
            },
          ]
        : []),
    ],
  };

  const updateTableYScroll = () => {
    if (tableContainerRef?.current) {
      const tableContainerHeight = tableContainerRef.current.offsetHeight;

      const paginationTotalHeight = Array.from(
        tableContainerRef.current.querySelectorAll(".ant-pagination"),
      ).reduce(
        (previousValue, currentElm) =>
          previousValue + currentElm.offsetHeight + TABLE_PAGINATION_MARGIN,
        0,
      );

      setTableScrollY(
        tableContainerHeight -
          (noHeightLimit ? 0 : paginationTotalHeight + TABLE_HEADER_HEIGHT) -
          5, // Extra height for table borders
      );
    }
  };

  useEffect(() => {
    updateTableYScroll();
  }, [tableRef.current?.offsetHeight]);

  useEffect(() => {
    const onResize = () => updateTableYScroll();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleChange = (paginationConfig, filters, sorter) => {
    const page = paginationConfig.current;
    const { pageSize } = paginationConfig;

    updateSearchParams({
      page,
      pageSize,
    });

    onChange?.(paginationConfig, filters, sorter);
  };

  return (
    <div className="flex-1 overflow-hidden" ref={tableContainerRef}>
      <Spin spinning={loading ?? false}>
        <Table
          ref={tableRef}
          scroll={tableScrollY ? { y: tableScrollY } : null}
          onChange={handleChange}
          {...tableProps}
          {...(hidePagination
            ? {}
            : {
                pagination: {
                  position: ["topRight", "bottomRight"],
                  showTotal: (total, range) => (
                    <Paginator total={total} range={range} />
                  ),
                  ...tableProps.pagination,
                },
              })}
        />
      </Spin>
    </div>
  );
};

TableView.propTypes = {
  actionMenus: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  onActionMenu: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func,
  hidePagination: PropTypes.bool,
  noHeightLimit: PropTypes.bool,
  loading: PropTypes.bool,
};

export default TableView;
